import {
  GoogleAnalyticsAccount,
  GoogleAnalyticsProperty,
  parseGoogleAnalyticsAccount,
} from "@/schemas/firestore/googleAnalytics.schema";
import { GoogleAnalyticsUIState } from "@/types/GoogleAnalytics";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface GoogleAnalyticsSlice {
  /* state */
  accounts: GoogleAnalyticsAccount[];
  uiState: GoogleAnalyticsUIState;
  errors: Record<string, string>;

  /* actions */
  // Multi-account actions
  setAccounts: (accounts: GoogleAnalyticsAccount[]) => void;
  addAccount: (account: GoogleAnalyticsAccount) => void;
  updateAccount: (
    accountId: string,
    updates: Partial<GoogleAnalyticsAccount>,
  ) => void;
  removeAccount: (accountId: string) => void;
  setUIState: (updates: Partial<GoogleAnalyticsUIState>) => void;
  setErrorForAccount: (accountId: string, error: string | null) => void;
  resetGA4State: () => void;

  // Utility getters (no domain-specific logic)
  getAccountById: (accountId: string) => GoogleAnalyticsAccount | null;
  getPropertiesForAccount: (accountId: string) => GoogleAnalyticsProperty[];
  hasConnectedAccounts: () => boolean;
}

const initialUIState: GoogleAnalyticsUIState = {
  isLoadingAccounts: false,
  isConnecting: false,
  isLoadingStatus: {},
  isLoadingProperties: {},
  isDisconnecting: {},
};

export const createGoogleAnalyticsSlice: StateCreator<
  StoreState,
  [],
  [],
  GoogleAnalyticsSlice
> = (set, get) => ({
  /* state */
  accounts: [],
  uiState: initialUIState,
  errors: {},

  /* actions */
  // Multi-account actions
  setAccounts: (accounts) => {
    // Validate all accounts before setting
    const validatedAccounts = accounts.map((account, index) => {
      try {
        return parseGoogleAnalyticsAccount(account);
      } catch (error) {
        console.error(
          `[googleAnalyticsSlice] Validation error for account at index ${index}:`,
          error instanceof Error ? error.message : "Unknown error",
          "Raw data:",
          account,
        );
        throw error;
      }
    });
    set({ accounts: validatedAccounts });
  },

  addAccount: (account) => {
    // Validate account before adding
    try {
      const validatedAccount = parseGoogleAnalyticsAccount(account);
      set((state) => ({
        accounts: [
          ...state.accounts.filter(
            (a) => a.account_id !== validatedAccount.account_id,
          ),
          validatedAccount,
        ],
      }));
    } catch (error) {
      console.error(
        `[googleAnalyticsSlice] Validation error for account ${account.account_id}:`,
        error instanceof Error ? error.message : "Unknown error",
        "Raw data:",
        account,
      );
      throw error;
    }
  },

  updateAccount: (accountId, updates) => {
    set((state) => ({
      accounts: state.accounts.map((account) => {
        if (account.account_id === accountId) {
          const updatedAccount = { ...account, ...updates };
          // Validate the merged account
          try {
            return parseGoogleAnalyticsAccount(updatedAccount);
          } catch (error) {
            console.error(
              `[googleAnalyticsSlice] Validation error for updated account ${accountId}:`,
              error instanceof Error ? error.message : "Unknown error",
              "Raw data:",
              updatedAccount,
            );
            throw error;
          }
        }
        return account;
      }),
    }));
  },

  removeAccount: (accountId) => {
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [accountId]: _, ...errors } = state.errors;
      return {
        accounts: state.accounts.filter(
          (account) => account.account_id !== accountId,
        ),
        errors,
      };
    });
  },

  setUIState: (updates) => {
    set((state) => ({
      uiState: { ...state.uiState, ...updates },
    }));
  },

  setErrorForAccount: (accountId, error) => {
    set((state) => {
      if (error === null) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [accountId]: _, ...rest } = state.errors;
        return { errors: rest };
      }
      return {
        errors: {
          ...state.errors,
          [accountId]: error,
        },
      };
    });
  },

  resetGA4State: () => {
    set({
      accounts: [],
      uiState: initialUIState,
      errors: {},
    });
  },

  // Utility getters (no domain-specific logic)
  getAccountById: (accountId) => {
    const state = get();
    return (
      state.accounts.find((account) => account.account_id === accountId) || null
    );
  },

  getPropertiesForAccount: (accountId) => {
    const account = get().getAccountById(accountId);
    return account?.properties || [];
  },

  hasConnectedAccounts: () => {
    const state = get();
    return state.accounts.some((account) => account.status === "connected");
  },
});
